<template>
  <div class="insurance-detail">
    <header class="section-header">
      <div>
        <button class="ghost" @click="$router.push('/dashboard/insurances')">
          ← Volver
        </button>
        <h1>{{ policy?.name || "Seguro" }}</h1>
        <p class="muted">{{ policy?.provider?.name || "Sin proveedor" }}</p>
      </div>
      <div class="record-meta">
        <span v-if="policy" class="chip" :class="statusClass(policy.status)">
          {{ statusLabel(policy.status) }}
        </span>
        <span v-if="policy" class="chip ghost">{{ categoryLabel(policy.category) }}</span>
        <span v-if="policy?.renewalDate" class="chip warning">
          Renueva en {{ daysUntil(policy.renewalDate) }} días
        </span>
      </div>
    </header>

    <section v-if="loading" class="state">Cargando seguro...</section>
    <section v-if="error" class="state error">{{ error }}</section>

    <section v-if="policy && !loading" class="detail-grid">
      <div class="card">
        <h2>Datos</h2>
        <div class="grid">
          <div>
            <p class="muted">Importe</p>
            <h3>{{ formatEur(policy.priceAmount) }}</h3>
          </div>
          <div>
            <p class="muted">Frecuencia</p>
            <h3>{{ paymentLabel(policy.paymentFrequency) }}</h3>
          </div>
          <div>
            <p class="muted">Renovación</p>
            <h3>{{ formatDate(policy.renewalDate) }}</h3>
          </div>
          <div>
            <p class="muted">Inicio</p>
            <h3>{{ formatDate(policy.startDate) }}</h3>
          </div>
          <div v-if="policy.policyNumber">
            <p class="muted">Póliza</p>
            <h3>{{ policy.policyNumber }}</h3>
          </div>
          <div v-if="policy.paymentMethod">
            <p class="muted">Método</p>
            <h3>{{ policy.paymentMethod }}</h3>
          </div>
          <div v-if="policy.phone">
            <p class="muted">Teléfono</p>
            <h3>{{ policy.phone }}</h3>
          </div>
          <div v-if="policy.email">
            <p class="muted">Email</p>
            <h3>{{ policy.email }}</h3>
          </div>
        </div>
        <p v-if="policy.notes" class="notes">{{ policy.notes }}</p>
      </div>

      <div class="card">
        <div class="card-header">
          <h2>Documentos</h2>
          <button class="ghost" @click="showUpload = !showUpload">
            {{ showUpload ? "Cancelar" : "+ Subir documento" }}
          </button>
        </div>

        <div v-if="showUpload" class="upload-box">
          <div class="row">
            <label>
              Título
              <input v-model="docForm.title" type="text" placeholder="Póliza, recibo..." />
            </label>
            <label>
              Tipo
              <select v-model="docForm.type">
                <option value="POLICY">Póliza</option>
                <option value="RECEIPT">Recibo</option>
                <option value="CONDITIONS">Condiciones</option>
                <option value="CLAIM">Siniestro</option>
                <option value="OTHER">Otro</option>
              </select>
            </label>
          </div>
          <label>
            Notas
            <textarea v-model="docForm.notes" rows="2"></textarea>
          </label>
          <label class="file">
            Archivo
            <input type="file" @change="onFileChange" />
          </label>
          <p v-if="docError" class="state error">{{ docError }}</p>
          <button class="primary" :disabled="uploading" @click="uploadDocument">
            {{ uploading ? "Subiendo..." : "Subir" }}
          </button>
        </div>

        <div v-if="!documents.length" class="empty-card">
          <p>No hay documentos todavía.</p>
        </div>
        <div v-for="doc in documents" :key="doc.id" class="doc-card">
          <div>
            <strong>{{ doc.title }}</strong>
            <p class="muted small">{{ typeLabel(doc.type) }}</p>
            <p class="muted small">{{ formatDateTime(doc.uploadedAt) }}</p>
          </div>
          <div class="doc-actions">
            <a v-if="doc.resource?.data" class="ghost" :href="doc.resource.data" target="_blank">
              Ver
            </a>
            <button class="ghost danger" @click="removeDocument(doc)">Eliminar</button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import {
  fetchInsurance,
  fetchInsuranceDocuments,
  uploadInsuranceDocuments,
  deleteInsuranceDocument,
} from "../services/insurances";

export default {
  name: "InsuranceDetailView",
  data() {
    return {
      policy: null,
      documents: [],
      loading: false,
      error: "",
      docError: "",
      showUpload: false,
      uploading: false,
      docForm: {
        title: "",
        type: "POLICY",
        notes: "",
        file: null,
      },
    };
  },
  async mounted() {
    await this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      this.error = "";
      try {
        const id = this.$route.params.id;
        this.policy = await fetchInsurance(id);
        this.documents = await fetchInsuranceDocuments(id);
      } catch (err) {
        this.error = err?.message || "No se pudo cargar el seguro.";
      } finally {
        this.loading = false;
      }
    },
    onFileChange(event) {
      const file = event.target.files?.[0];
      this.docForm.file = file || null;
    },
    async uploadDocument() {
      this.docError = "";
      if (!this.docForm.file) {
        this.docError = "Selecciona un archivo.";
        return;
      }
      this.uploading = true;
      try {
        const base64 = await this.toBase64(this.docForm.file);
        const payload = [
          {
            title: this.docForm.title || this.docForm.file.name,
            type: this.docForm.type,
            notes: this.docForm.notes || null,
            file: {
              name: this.docForm.file.name,
              data: base64,
              type: "FILE",
              format: "BASE64",
            },
          },
        ];
        await uploadInsuranceDocuments(this.$route.params.id, payload);
        this.docForm = { title: "", type: "POLICY", notes: "", file: null };
        this.showUpload = false;
        this.documents = await fetchInsuranceDocuments(this.$route.params.id);
      } catch (err) {
        this.docError = err?.message || "No se pudo subir el documento.";
      } finally {
        this.uploading = false;
      }
    },
    async removeDocument(doc) {
      if (!confirm("Eliminar documento?")) return;
      try {
        await deleteInsuranceDocument(this.$route.params.id, doc.id);
        this.documents = this.documents.filter((d) => d.id !== doc.id);
      } catch (err) {
        this.docError = err?.message || "No se pudo eliminar.";
      }
    },
    toBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    formatDate(value) {
      if (!value) return "-";
      return new Date(value).toLocaleDateString();
    },
    formatDateTime(value) {
      if (!value) return "-";
      return new Date(value).toLocaleString();
    },
    formatEur(amount) {
      if (amount === null || amount === undefined) return "-";
      return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(amount);
    },
    categoryLabel(value) {
      return {
        CAR: "Coche",
        HOME: "Hogar",
        HEALTH: "Salud",
        LIFE: "Vida",
        OTHER: "Otros",
      }[value] || "Otros";
    },
    paymentLabel(value) {
      return {
        MONTHLY: "Mensual",
        QUARTERLY: "Trimestral",
        YEARLY: "Anual",
        ONE_TIME: "Único",
      }[value] || "Anual";
    },
    statusLabel(value) {
      return {
        ACTIVE: "Activo",
        CANCELLED: "Cancelado",
        EXPIRED: "Caducado",
      }[value] || "Activo";
    },
    statusClass(value) {
      return {
        ACTIVE: "success",
        CANCELLED: "ghost",
        EXPIRED: "danger",
      }[value] || "ghost";
    },
    daysUntil(date) {
      if (!date) return 0;
      const diff = new Date(date).getTime() - Date.now();
      return Math.max(0, Math.ceil(diff / 86400000));
    },
    typeLabel(value) {
      return {
        POLICY: "Póliza",
        RECEIPT: "Recibo",
        CONDITIONS: "Condiciones",
        CLAIM: "Siniestro",
        OTHER: "Otro",
      }[value] || "Otro";
    },
  },
};
</script>

<style scoped>
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
}

.muted {
  color: var(--muted);
  margin: 0;
}

.muted.small {
  font-size: 12px;
}

.record-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.detail-grid {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.8fr);
}

.state {
  padding: 18px;
  text-align: center;
  color: var(--muted);
}

.state.error {
  color: var(--danger);
}

.card {
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 18px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.notes {
  margin-top: 12px;
}

.doc-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--panel-2);
  margin-top: 10px;
}

.doc-actions {
  display: flex;
  gap: 8px;
}

.upload-box {
  border: 1px dashed var(--border);
  border-radius: 14px;
  padding: 14px;
  margin-bottom: 16px;
  background: rgba(20, 33, 59, 0.5);
}

.chip {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: var(--text);
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 12px;
}

.chip.ghost {
  background: rgba(148, 163, 184, 0.1);
  border-color: rgba(148, 163, 184, 0.2);
  color: var(--muted);
}

.chip.warning {
  background: rgba(251, 191, 36, 0.16);
  border-color: rgba(251, 191, 36, 0.45);
  color: #fcd34d;
}

.chip.success {
  background: rgba(34, 197, 94, 0.18);
  color: #22c55e;
}

.chip.danger {
  background: rgba(248, 113, 113, 0.2);
  color: #f87171;
}

.ghost {
  background: rgba(148, 163, 184, 0.12);
  border: 1px solid rgba(148, 163, 184, 0.25);
  color: var(--text);
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
}

.ghost.danger {
  color: var(--danger);
}

.primary {
  background: linear-gradient(120deg, var(--accent), var(--success));
  color: #071022;
  border: none;
  padding: 10px 18px;
  border-radius: 999px;
  font-weight: 700;
  cursor: pointer;
}

.empty-card {
  border: 1px dashed rgba(148, 163, 184, 0.3);
  border-radius: 16px;
  padding: 16px;
  text-align: center;
  color: var(--muted);
}

@media (max-width: 900px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
